import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
  ScrollView,
  ActivityIndicator,
  Image,
  Alert,
} from 'react-native';
import { BlurView } from 'expo-blur';
import { MaterialIcons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';

// Three-step host troubleshooting flow shown when a charger reports a fault.
// Steps:
//   1. Restart the charger (mocked: simulates a request, reports fault still
//      present so the host has reason to continue)
//   2. Check cable connections (advisory; user confirms)
//   3. Photograph the charger and cable, submit a support ticket (mocked:
//      shows a fake ticket number on completion)
// This is a customer-facing demo flow — none of the network calls are real.

const deviceWidth = Dimensions.get('window').width;
const horizontalPadding = 20;
const modalWidth = deviceWidth - horizontalPadding * 2;

type Step = 'restart' | 'cable' | 'photos' | 'submitted';

interface FaultTroubleshootingModalProps {
  visible: boolean;
  onClose: () => void;
  chargerName: string;
  errorCode?: string | null;
  errorUserMessage?: string | null;
}

type PhotoSlot = 'charger' | 'cable';

interface PhotoState {
  charger: string | null;
  cable: string | null;
}

const FaultTroubleshootingModal: React.FC<FaultTroubleshootingModalProps> = ({
  visible,
  onClose,
  chargerName,
  errorCode,
  errorUserMessage,
}) => {
  const [step, setStep] = useState<Step>('restart');
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartReported, setRestartReported] = useState(false);
  const [photos, setPhotos] = useState<PhotoState>({
    charger: null,
    cable: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setStep('restart');
      setRestartLoading(false);
      setRestartReported(false);
      setPhotos({ charger: null, cable: null });
      setSubmitting(false);
      setTicketNumber(null);
    }
  }, [visible]);

  const handleRestart = async () => {
    setRestartLoading(true);
    setRestartReported(false);
    await new Promise((r) => setTimeout(r, 1800));
    setRestartLoading(false);
    setRestartReported(true);
  };

  const pickPhoto = async (slot: PhotoSlot) => {
    try {
      const camPerm = await ImagePicker.requestCameraPermissionsAsync();
      let uri: string | null = null;

      if (camPerm.status === 'granted') {
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: ['images'],
          allowsEditing: false,
          quality: 0.7,
        });
        if (!result.canceled && result.assets?.length) {
          uri = result.assets[0].uri;
        }
      } else {
        const libPerm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (libPerm.status !== 'granted') {
          Alert.alert(
            'Permission needed',
            'Camera or photo library access is required to attach photos.'
          );
          return;
        }
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          allowsEditing: false,
          quality: 0.7,
        });
        if (!result.canceled && result.assets?.length) {
          uri = result.assets[0].uri;
        }
      }

      if (uri) {
        setPhotos((p) => ({ ...p, [slot]: uri }));
      }
    } catch (e) {
      console.error('[FaultTroubleshooting] pickPhoto error:', e);
      Alert.alert('Error', 'Could not attach photo. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!photos.charger || !photos.cable) {
      Alert.alert(
        'Photos required',
        'Please attach a photo of the charger and a photo of the cable so our support team can diagnose the fault.'
      );
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    // Deterministic-ish ticket id
    const id = 4000 + Math.floor(Math.random() * 999);
    setTicketNumber(`#${id}`);
    setSubmitting(false);
    setStep('submitted');
  };

  const renderHeader = () => (
    <View style={styles.headerContainer}>
      <View style={styles.statusIconContainer}>
        <MaterialIcons name="build" size={28} color="#FFFFFF" />
      </View>
      <Text style={styles.headerText}>Troubleshoot {chargerName}</Text>
      {errorUserMessage && (
        <Text style={styles.subHeaderText} numberOfLines={2}>
          {errorUserMessage}
        </Text>
      )}
      {errorCode && (
        <View style={styles.errorChip}>
          <Text style={styles.errorChipText}>Error: {errorCode}</Text>
        </View>
      )}
    </View>
  );

  const renderStepIndicator = (current: Step) => {
    if (current === 'submitted') return null;
    const steps: Step[] = ['restart', 'cable', 'photos'];
    return (
      <View style={styles.stepIndicator}>
        {steps.map((s, i) => {
          const isCurrent = s === current;
          const isPast = steps.indexOf(current) > i;
          return (
            <React.Fragment key={s}>
              <View
                style={[
                  styles.stepDot,
                  isCurrent && styles.stepDotCurrent,
                  isPast && styles.stepDotPast,
                ]}
              >
                {isPast ? (
                  <MaterialIcons name="check" size={14} color="#FFFFFF" />
                ) : (
                  <Text
                    style={[
                      styles.stepDotText,
                      isCurrent && styles.stepDotTextCurrent,
                    ]}
                  >
                    {i + 1}
                  </Text>
                )}
              </View>
              {i < steps.length - 1 && (
                <View
                  style={[
                    styles.stepConnector,
                    isPast && styles.stepConnectorPast,
                  ]}
                />
              )}
            </React.Fragment>
          );
        })}
      </View>
    );
  };

  const renderRestartStep = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Step 1 — Restart the charger</Text>
      <Text style={styles.bodyText}>
        Power-cycling clears most transient faults. We'll send a remote restart
        command and check whether the charger comes back online.
      </Text>

      <TouchableOpacity
        style={[styles.primaryButton, restartLoading && styles.buttonDisabled]}
        onPress={handleRestart}
        disabled={restartLoading}
      >
        {restartLoading ? (
          <>
            <ActivityIndicator color="#FFFFFF" size="small" />
            <Text style={styles.primaryButtonText}>Restarting…</Text>
          </>
        ) : (
          <>
            <MaterialIcons name="restart-alt" size={20} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>
              {restartReported ? 'Restart again' : 'Restart Charger'}
            </Text>
          </>
        )}
      </TouchableOpacity>

      {restartReported && (
        <View style={styles.resultBox}>
          <MaterialIcons name="info" size={20} color="#F59E0B" />
          <Text style={styles.resultBoxText}>
            Restart sent. The charger came back online but is still reporting a
            fault. Continue to the next step.
          </Text>
        </View>
      )}

      {restartReported && (
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={() => setStep('cable')}
        >
          <Text style={styles.secondaryButtonText}>Next: check cable</Text>
          <MaterialIcons name="arrow-forward" size={18} color="#51B868" />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderCableStep = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Step 2 — Check cable connections</Text>
      <Text style={styles.bodyText}>
        Loose connectors are a common cause of ground faults and lockouts.
        Visually inspect both ends of the cable:
      </Text>

      <View style={styles.checklistItem}>
        <MaterialIcons name="bolt" size={20} color="#51B868" />
        <Text style={styles.checklistText}>
          Unplug the connector from the charger, inspect for debris or
          corrosion, and re-seat firmly until it clicks.
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <MaterialIcons name="electric-car" size={20} color="#51B868" />
        <Text style={styles.checklistText}>
          Unplug the connector from the vehicle, confirm the port is dry and
          clean, and re-seat firmly.
        </Text>
      </View>
      <View style={styles.checklistItem}>
        <MaterialIcons name="warning" size={20} color="#F59E0B" />
        <Text style={styles.checklistText}>
          If you see visible damage to the cable or connector, do not use the
          charger — proceed to step 3 to open a ticket.
        </Text>
      </View>

      <TouchableOpacity
        style={styles.primaryButton}
        onPress={() => setStep('photos')}
      >
        <Text style={styles.primaryButtonText}>
          Fault still showing — open a ticket
        </Text>
        <MaterialIcons name="arrow-forward" size={18} color="#FFFFFF" />
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.tertiaryButton}
        onPress={() => setStep('restart')}
      >
        <MaterialIcons name="arrow-back" size={16} color="#9CA3AF" />
        <Text style={styles.tertiaryButtonText}>Back to restart</Text>
      </TouchableOpacity>
    </View>
  );

  const renderPhotoSlot = (slot: PhotoSlot, label: string, icon: keyof typeof MaterialIcons.glyphMap) => (
    <TouchableOpacity
      style={[styles.photoSlot, photos[slot] && styles.photoSlotFilled]}
      onPress={() => pickPhoto(slot)}
    >
      {photos[slot] ? (
        <>
          <Image source={{ uri: photos[slot] as string }} style={styles.photoThumb} />
          <View style={styles.photoOverlay}>
            <MaterialIcons name="check-circle" size={20} color="#51B868" />
            <Text style={styles.photoSlotLabelFilled}>{label}</Text>
          </View>
        </>
      ) : (
        <>
          <MaterialIcons name={icon} size={32} color="#9CA3AF" />
          <Text style={styles.photoSlotLabel}>{label}</Text>
          <Text style={styles.photoSlotHint}>Tap to capture</Text>
        </>
      )}
    </TouchableOpacity>
  );

  const renderPhotosStep = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Step 3 — Submit photos to support</Text>
      <Text style={styles.bodyText}>
        Attach a photo of the charger and a photo of the cable. Our support
        team will follow up by email within 24 hours.
      </Text>

      <View style={styles.photoRow}>
        {renderPhotoSlot('charger', 'Photo of charger', 'ev-station')}
        {renderPhotoSlot('cable', 'Photo of cable', 'cable')}
      </View>

      <TouchableOpacity
        style={[styles.primaryButton, submitting && styles.buttonDisabled]}
        onPress={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <ActivityIndicator color="#FFFFFF" size="small" />
            <Text style={styles.primaryButtonText}>Submitting…</Text>
          </>
        ) : (
          <>
            <MaterialIcons name="send" size={18} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Submit to Support</Text>
          </>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.tertiaryButton}
        onPress={() => setStep('cable')}
      >
        <MaterialIcons name="arrow-back" size={16} color="#9CA3AF" />
        <Text style={styles.tertiaryButtonText}>Back to cable check</Text>
      </TouchableOpacity>
    </View>
  );

  const renderSubmittedStep = () => (
    <View style={styles.section}>
      <View style={styles.successIconContainer}>
        <MaterialIcons name="check-circle" size={56} color="#51B868" />
      </View>
      <Text style={styles.successTitle}>Ticket {ticketNumber} created</Text>
      <Text style={styles.bodyText}>
        Our support team will review the photos and reach out within 24 hours.
        You'll see status updates here and via email.
      </Text>

      <View style={styles.ticketSummary}>
        <View style={styles.ticketRow}>
          <Text style={styles.ticketLabel}>Charger</Text>
          <Text style={styles.ticketValue}>{chargerName}</Text>
        </View>
        {errorCode && (
          <View style={styles.ticketRow}>
            <Text style={styles.ticketLabel}>Error code</Text>
            <Text style={styles.ticketValue}>{errorCode}</Text>
          </View>
        )}
        <View style={styles.ticketRow}>
          <Text style={styles.ticketLabel}>Attachments</Text>
          <Text style={styles.ticketValue}>2 photos</Text>
        </View>
        <View style={styles.ticketRow}>
          <Text style={styles.ticketLabel}>Priority</Text>
          <Text style={styles.ticketValue}>High</Text>
        </View>
      </View>

      <TouchableOpacity style={styles.primaryButton} onPress={onClose}>
        <Text style={styles.primaryButtonText}>Done</Text>
      </TouchableOpacity>
    </View>
  );

  const renderStep = () => {
    switch (step) {
      case 'restart':
        return renderRestartStep();
      case 'cable':
        return renderCableStep();
      case 'photos':
        return renderPhotosStep();
      case 'submitted':
        return renderSubmittedStep();
    }
  };

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.absoluteContainer}>
        <BlurView intensity={50} tint="dark" style={styles.modalBackground}>
          <Pressable style={styles.backdrop} onPress={onClose} />

          <View style={styles.modalContainer}>
            {renderHeader()}
            {renderStepIndicator(step)}
            <View style={styles.separator} />

            <ScrollView
              style={styles.scrollContent}
              contentContainerStyle={styles.scrollContentContainer}
              showsVerticalScrollIndicator
              bounces
            >
              {renderStep()}
            </ScrollView>

            {step !== 'submitted' && (
              <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                <Text style={styles.closeButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        </BlurView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  absoluteContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalBackground: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContainer: {
    backgroundColor: '#292D33',
    width: modalWidth * 0.95,
    maxWidth: 440,
    maxHeight: '90%',
    padding: 24,
    borderRadius: 16,
    alignItems: 'center',
    flexShrink: 1,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 12,
    alignSelf: 'stretch',
  },
  statusIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#EF4444',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerText: {
    fontFamily: 'PlusJakartaSans-Bold',
    fontWeight: '700',
    fontSize: 20,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 6,
  },
  subHeaderText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 13,
    color: '#B0B1B3',
    textAlign: 'center',
    marginBottom: 8,
    paddingHorizontal: 8,
  },
  errorChip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    backgroundColor: '#1E2227',
  },
  errorChipText: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 11,
    color: '#F59E0B',
  },
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 8,
  },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#1E2227',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepDotCurrent: {
    backgroundColor: '#51B868',
  },
  stepDotPast: {
    backgroundColor: '#51B868',
  },
  stepDotText: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 12,
    color: '#9CA3AF',
  },
  stepDotTextCurrent: {
    color: '#FFFFFF',
  },
  stepConnector: {
    width: 28,
    height: 2,
    backgroundColor: '#1E2227',
    marginHorizontal: 4,
  },
  stepConnectorPast: {
    backgroundColor: '#51B868',
  },
  separator: Platform.select({
    web: {
      alignSelf: 'stretch',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
      borderColor: '#5F6164',
      marginVertical: 12,
    },
    default: {
      alignSelf: 'stretch',
      borderBottomWidth: 1,
      borderColor: '#5F6164',
      marginVertical: 12,
    },
  }),
  scrollContent: {
    alignSelf: 'stretch',
    flexGrow: 1,
    flexShrink: 1,
  },
  scrollContentContainer: {
    paddingBottom: 8,
  },
  section: {
    alignSelf: 'stretch',
  },
  sectionTitle: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 14,
    color: '#51B868',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  bodyText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 14,
    color: '#FFFFFF',
    lineHeight: 20,
    marginBottom: 12,
  },
  primaryButton: {
    backgroundColor: '#51B868',
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-Bold',
    fontWeight: '700',
    fontSize: 15,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  secondaryButton: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
  },
  secondaryButtonText: {
    color: '#51B868',
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 14,
  },
  tertiaryButton: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
  },
  tertiaryButtonText: {
    color: '#9CA3AF',
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 13,
  },
  resultBox: {
    flexDirection: 'row',
    gap: 8,
    backgroundColor: '#1E2227',
    padding: 12,
    borderRadius: 10,
    marginTop: 12,
    alignItems: 'flex-start',
  },
  resultBoxText: {
    flex: 1,
    color: '#B0B1B3',
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 13,
    lineHeight: 18,
  },
  checklistItem: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
    marginVertical: 6,
    backgroundColor: '#1E2227',
    padding: 12,
    borderRadius: 10,
  },
  checklistText: {
    flex: 1,
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 13,
    lineHeight: 18,
  },
  photoRow: {
    flexDirection: 'row',
    gap: 12,
    marginVertical: 14,
  },
  photoSlot: {
    flex: 1,
    aspectRatio: 1,
    borderRadius: 12,
    backgroundColor: '#1E2227',
    borderWidth: 2,
    borderColor: '#3A3D42',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    overflow: 'hidden',
  },
  photoSlotFilled: {
    borderStyle: 'solid',
    borderColor: '#51B868',
  },
  photoSlotLabel: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 6,
  },
  photoSlotLabelFilled: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 11,
  },
  photoSlotHint: {
    color: '#9CA3AF',
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 10,
    textAlign: 'center',
    marginTop: 2,
  },
  photoThumb: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  photoOverlay: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    right: 6,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  closeButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#5F6164',
    borderRadius: 45,
    width: '100%',
    height: 46,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 14,
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 15,
  },
  successIconContainer: {
    alignItems: 'center',
    marginBottom: 10,
  },
  successTitle: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-Bold',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 10,
  },
  ticketSummary: {
    backgroundColor: '#1E2227',
    borderRadius: 10,
    padding: 12,
    marginVertical: 12,
  },
  ticketRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  ticketLabel: {
    color: '#9CA3AF',
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 13,
  },
  ticketValue: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontSize: 13,
  },
});

export default FaultTroubleshootingModal;
